import { DateTime } from 'luxon';
import type { ParsedParams } from '../config.ts';
import { PresentCheck, checkFail } from '../core.ts';
import { isAbsent, isPresent } from '../identity.ts';
import { standardDate, standardTime } from './standard.ts';
export { DateTime } from 'luxon';

const reDateSlash = /^(?<month>[01]?[0-9])\/(?<day>[0-3]?[0-9])(\/(?<year>[0-9]{2,4}))?$/;
const reDateDot = /^(?<day>[0-3]?[0-9])\.(?<month>[01]?[0-9])(\.(?<year>[0-9]{2,4}))?$/;

// DNS: unittest
// 2020-01-30
// 01/30/2020
// 30.01.2020
// 1/30 (current year)
// 30.1 (current year)
export function parseLocalDate(
  date: string | null | undefined,
  referenceDate?: DateTime,
): DateTime | undefined;
export function parseLocalDate(
  date: string | null | undefined,
  referenceDate: DateTime | undefined,
  check: typeof PresentCheck,
): DateTime;
export function parseLocalDate(
  date: string | null | undefined,
  referenceDate: DateTime | undefined,
  check?: typeof PresentCheck,
): DateTime | undefined {
  if (isPresent(date)) {
    let result = DateTime.fromISO(date);
    if (result.isValid) {
      return result;
    }
    const match = date.match(reDateSlash) ?? date.match(reDateDot);
    if (match?.groups) {
      const month = match.groups.month.padStart(2, '0');
      const day = match.groups.day.padStart(2, '0');
      let year = match.groups.year;
      if (isAbsent(year) || year.length < 4) {
        referenceDate ??= DateTime.now();
        const referenceYear = String(referenceDate.year);
        year = isPresent(year) ? referenceYear.slice(0, -2) + year : referenceYear;
      }
      result = DateTime.fromISO(`${year}-${month}-${day}`);
      if (result.isValid) {
        return result;
      }
    }
  }
  if (check) {
    checkFail(
      'parseLocalDate failed.',
      isPresent(date) ? 'Input was absent.' : `Input ${date} could not be parsed.`,
    );
  }
  return undefined;
}

export function parseDateTime(params: ParsedParams, type: 'datetime' | 'time' | 'date') {
  const result = parseLocalDate(params.peek());
  if (isAbsent(result)) {
    return result;
  }
  params.consume();
  switch (type) {
    case 'date':
      return standardDate(result);
    case 'time':
      return standardTime(result);
    default:
      return result;
  }
}

// 2020-01-02
// 2020-01-02T03:04:05.06Z
// If the input date is Gregorian year 0, we coerce it to 1.
export function fromRfc3339(date: string | null | undefined): DateTime<true> | undefined;
export function fromRfc3339(
  date: string | null | undefined,
  check: typeof PresentCheck,
): DateTime<true>;
export function fromRfc3339(
  date: string | null | undefined,
  check?: typeof PresentCheck,
): DateTime | undefined {
  if (isPresent(date)) {
    let result = DateTime.fromISO(date);
    if (result.isValid) {
      if (result.year === 0) {
        result = result.set({ year: 1 });
      }
      return result;
    }
  }
  if (check) {
    checkFail(
      'fromRfc3339 failed.',
      isPresent(date) ? 'Input was absent.' : `Input ${date} could not be parsed.`,
    );
  }
  return undefined;
}

// Sat, 11 May 2024 02:58:22 GMT
export function fromHttp(date: string | undefined) {
  if (isPresent(date)) {
    const result = DateTime.fromHTTP(date);
    if (result.isValid) {
      return result;
    }
  }
  return undefined;
}
